import usePlanLimits from 'hooks/plan/usePlanLimits';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UsageProgressBadge, UsageProgressCardProps } from '../../usage-progress-card/models/usage-progress-card-props';
import { useShowFreeTrial } from './useShowFreeTrial';
import { useUpgradePlan } from 'hooks/billing/useUpgradePlan';
import { UsageStatus } from '../models/usage-status';

const getUsageStatus = (progress: number): UsageStatus => {
    if (progress >= 100) return UsageStatus.LIMIT_REACHED;
    if (progress >= 75) return UsageStatus.ALMOST_FULL;
    if (progress >= 50) return UsageStatus.RUNNING_LOW;
    return UsageStatus.ON_TRACK;
};

interface UsageStatusConfig {
    progressTone: UsageProgressCardProps['progress']['tone'];
    /** No badge when omitted */
    badge?: UsageProgressBadge;
    description: string;
    /** Label of the upgrade button */
    actionLabel: string;
}

export interface MonthlyOrderUsage extends UsageStatusConfig {
    status: UsageStatus;
    used: number;
    limit: number;
    /** Percentage of the limit used, capped at 100 */
    progress: number;
}

// The monthly order limit resets on the first day of the next month
const getLimitResetDate = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 1);
};

/**
 * This month's order usage against the current plan's monthly order limit,
 * with the matching progress tone, status badge, description and upgrade button label.
 */
export const useMonthlyOrderUsage = (): MonthlyOrderUsage => {
    const { t, i18n } = useTranslation();
    const { getMonthlyOrderLimit, refresh: refreshPlanLimits } = usePlanLimits();
    const { name: upgradePlanName, trialDays } = useUpgradePlan();
    // The trial wording also needs the plan's trial length, so it isn't shown as "0 days" (plans still
    // loading, or a plan without a trial)
    const showFreeTrial = useShowFreeTrial() && trialDays > 0;

    useEffect(() => {
        refreshPlanLimits();
    }, []);

    const orderLimit = getMonthlyOrderLimit();
    const { used, limit, progress, status } = useMemo(() => {
        const used = parseInt(orderLimit?.currentlyUsed, 10) || 0;
        const limit = parseInt(orderLimit?.value, 10) || 0;
        const progress = limit ? Math.min((used * 100) / limit, 100) : 0;
        return { used, limit, progress, status: getUsageStatus(progress) };
    }, [orderLimit?.currentlyUsed, orderLimit?.value]);

    const resetDate = useMemo(
        () =>
            // en-GB for "1 October" instead of the en-US "October 1"
            new Intl.DateTimeFormat(i18n.language === 'en' ? 'en-GB' : i18n.language, {
                day: 'numeric',
                month: 'long',
            }).format(getLimitResetDate()),
        [i18n.language]
    );

    const statusConfig = useMemo((): UsageStatusConfig => {
        const subscribeLabel = t('DASHBOARD.UPSELL_CARD.UPGRADE_PLAN', { plan: upgradePlanName });

        switch (status) {
            case UsageStatus.LIMIT_REACHED:
                return {
                    progressTone: 'critical',
                    badge: { tone: 'critical', label: t('DASHBOARD.USAGE_PROGRESS_CARD.STATUS.LIMIT_REACHED.BADGE') },
                    description: t('DASHBOARD.USAGE_PROGRESS_CARD.STATUS.LIMIT_REACHED.DESCRIPTION', {
                        date: resetDate,
                    }),
                    actionLabel: showFreeTrial
                        ? t('DASHBOARD.USAGE_PROGRESS_CARD.STATUS.LIMIT_REACHED.ACTION', { count: trialDays })
                        : subscribeLabel,
                };
            case UsageStatus.ALMOST_FULL:
                return {
                    progressTone: 'warning',
                    badge: { tone: 'warning', label: t('DASHBOARD.USAGE_PROGRESS_CARD.STATUS.ALMOST_FULL.BADGE') },
                    description: t('DASHBOARD.USAGE_PROGRESS_CARD.STATUS.ALMOST_FULL.DESCRIPTION', { date: resetDate }),
                    actionLabel: showFreeTrial
                        ? t('DASHBOARD.USAGE_PROGRESS_CARD.STATUS.ALMOST_FULL.ACTION', {
                              count: trialDays,
                              plan: upgradePlanName,
                          })
                        : subscribeLabel,
                };
            case UsageStatus.RUNNING_LOW:
                return {
                    progressTone: 'caution',
                    badge: { tone: 'caution', label: t('DASHBOARD.USAGE_PROGRESS_CARD.STATUS.RUNNING_LOW.BADGE') },
                    description: t('DASHBOARD.USAGE_PROGRESS_CARD.STATUS.RUNNING_LOW.DESCRIPTION', { date: resetDate }),
                    actionLabel: showFreeTrial
                        ? t('DASHBOARD.USAGE_PROGRESS_CARD.STATUS.RUNNING_LOW.ACTION', {
                              count: trialDays,
                              plan: upgradePlanName,
                          })
                        : subscribeLabel,
                };
            default:
                return {
                    progressTone: 'success',
                    description: t('DASHBOARD.USAGE_PROGRESS_CARD.STATUS.ON_TRACK.DESCRIPTION', { date: resetDate }),
                    actionLabel: showFreeTrial
                        ? t('DASHBOARD.USAGE_PROGRESS_CARD.STATUS.ON_TRACK.ACTION', { count: trialDays })
                        : subscribeLabel,
                };
        }
    }, [status, resetDate, showFreeTrial, trialDays, upgradePlanName, t]);

    return { status, used, limit, progress, ...statusConfig };
};
